<template>
  <div class="m-4">
    <h1 class="text-h1 mb-1">Accès en consultation aux parapheurs d'un autre utilisateur</h1>

    <div>Ajoute en copie l'utilisateur destinataire aux parapheurs de l'utilisateur source.</div>

    <div class="mb-4">
      Les parapheurs où l'utilisateur destinataire est déjà dans le flux ou en copie ne seront pas pris en compte.
    </div>

    <div>
      <div class="mb-1">Utilisateur source</div>
      <SelectUser v-model="userSource" />
    </div>

    <PCheckboxGroup v-model="lfType" label="Type de parapheur" data-sentry-id="PrefMailTypesCheckboxGroup" />

    <div>
      <div class="mb-1">Prendre en compte les parapheurs après le numéro de chrono</div>
      <PInput v-model="startChrono" label="Numéro chrono" class="w-fit" />
    </div>

    <div>
      <div class="mb-1">Ne pas prendre en compte les parapheurs créés après le</div>
      <PDateTimePicker
        label=""
        v-model="endDate"
        aria-label="Date de fin: format jj/mm/aaaa"
        class="w-fit"
        :teleport="true"
        data-sentry-id="TransferLfEndDatePicker"
      />
    </div>

    <div>
      <div class="mb-1">Utilisateur destinataire</div>
      <SelectUser v-model="userRecipient" />
    </div>

    <PButton :loading="loadingCountTransfer" :disabled="!userRecipient[0] || !userSource[0]" @click="countTransfer">
      Mettre en copie les parapheur
    </PButton>

    <PModal :title="`Transfére de ${nbLf} parapheur(s)`" v-model="openModal">
      <template #default>
        <p>
          Transférer {{ nbLf }} parapheurs(s) de {{ userSource[0]?.firstName }} {{ userSource[0]?.lastName }} à
          {{ userRecipient[0]?.firstName }} {{ userRecipient[0]?.lastName }}
        </p>

        <p v-if="nbLf > 0" class="mb-2">Confirmez-vous le transfert de ces parapheurs ?</p>
        <p v-else class="mb-2">Aucun parapheur ne correspond à ces critères, il n'y a rien à transférer.</p>

        <PLink v-if="exportLink" :to="exportLink" data-sentry-id="TransferLfExportLink">
          Télécharger la liste des parapheurs concernés
        </PLink>
        <PButton
          v-else-if="nbLf > 0"
          type="button"
          mode="tertiary"
          :loading="loadingExport"
          @click="exportCandidates"
          data-sentry-id="TransferLfExportBtn"
        >
          Télécharger la liste des parapheurs concernés
        </PButton>
      </template>

      <template #actions>
        <div class="flex justify-end gap-2">
          <PButton type="button" mode="tertiary" @click="openModal = false" data-sentry-id="TransferLfCancelBtn">
            Annuler
          </PButton>
          <PButton
            type="button"
            mode="primary"
            :disabled="nbLf === 0"
            :loading="loadingTransfer"
            @click="confirmTransfer"
            data-sentry-id="TransferLfConfirmBtn"
          >
            Confirmer
          </PButton>
        </div>
      </template>
    </PModal>
  </div>
</template>

<script lang="ts" setup>
import SelectUser from "../components/SelectUser.vue";
import { ref } from "vue";
import {
  type CheckboxOption,
  PCheckboxGroup,
  PInput,
  PDateTimePicker,
  PButton,
  PModal,
  PLink,
  useAlertManager,
  AlertType,
  AlertDuration,
} from "@pasta/box-v2";
import { type TransferLfDto } from "../dto/letter-file.dto";
import { useLetterFileStore } from "../stores/letter-file.store";
import { type AgentsDirectoryAutocompleteItem } from "../dto/agents-directory.dto";

enum LFTypes {
  FLUX_AMONT = "FLUX_AMONT",
  FLUX_AVAL = "FLUX_AVAL",
  CC = "CC",
  ARCHIVED = "ARCHIVED",
}

const letterFileStore = useLetterFileStore();
const alertManager = useAlertManager();

const userSource = ref<AgentsDirectoryAutocompleteItem[]>([]);
const userRecipient = ref<AgentsDirectoryAutocompleteItem[]>([]);
const startChrono = ref();
const endDate = ref<string>("");
const nbLf = ref(0);
const loadingCountTransfer = ref(false);
const loadingTransfer = ref(false);
const loadingExport = ref(false);
const openModal = ref(false);
const exportLink = ref<string | null>(null);

const lfType = ref<CheckboxOption[]>([
  {
    label: "L'utilisateur source est dans le flux amont",
    selected: true,
    value: LFTypes.FLUX_AMONT,
  },
  {
    label: "L'utilisateur source est dans le flux aval",
    selected: true,
    value: LFTypes.FLUX_AVAL,
  },
  {
    label: "L'utilisateur source est en copie",
    selected: true,
    value: LFTypes.CC,
  },
  {
    label: "Prendre en compte les parapheurs archivés",
    selected: false,
    value: LFTypes.ARCHIVED,
  },
]);

function buildDto(): TransferLfDto {
  const selected = lfType.value.filter(option => option.selected).map(option => option.value);
  return {
    sourceMail: userSource.value[0]?.mail,
    recipientMail: userRecipient.value[0]?.mail,
    includeUpstream: selected.includes(LFTypes.FLUX_AMONT),
    includeDownstream: selected.includes(LFTypes.FLUX_AVAL),
    includeCC: selected.includes(LFTypes.CC),
    includeArchived: selected.includes(LFTypes.ARCHIVED),
    afterChrono: startChrono.value || undefined,
    endDate: endDate.value || undefined,
  };
}

async function countTransfer() {
  loadingCountTransfer.value = true;
  try {
    const dto = buildDto();
    console.log("[transfer] countTransfer dto sent:", dto);
    const count = await letterFileStore.countTransferCandidates(dto);
    console.log("[transfer] countTransfer count received:", count);
    nbLf.value = count;
    exportLink.value = null;
    openModal.value = true;
  } finally {
    loadingCountTransfer.value = false;
  }
}

async function exportCandidates() {
  loadingExport.value = true;
  try {
    exportLink.value = await letterFileStore.exportTransferCandidates(buildDto());
  } finally {
    loadingExport.value = false;
  }
}

async function confirmTransfer() {
  loadingTransfer.value = true;
  try {
    await letterFileStore.transfer(buildDto());
    openModal.value = false;
    alertManager.createAlert({
      message: `${nbLf.value} parapheur(s) transféré(s) avec succès`,
      type: AlertType.SUCCESS,
      duration: AlertDuration.MEDIUM,
    });
  } finally {
    loadingTransfer.value = false;
  }
}
</script>
